import React from 'react';
import { Shield, Mail, Database, Trash2, HelpCircle, Smartphone, Send, ArrowLeftRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: (
        <>
          <p className="mb-3">We collect information necessary to provide our property management and communication services:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
            <li><strong>Contact Information:</strong> Phone numbers (including mobile numbers) provided during signup or in account settings, used for SMS messaging and account verification</li>
            <li><strong>Organization Details:</strong> Company name, contact phone number, and business description for landlords and property managers</li>
            <li><strong>Tenant Information:</strong> Names, email addresses, and phone numbers of tenants who sign up or are invited to the platform</li>
            <li><strong>Property & Unit Data:</strong> Property names, addresses, unit identifiers, and descriptions that establish the rental relationship context</li>
            <li><strong>Maintenance Requests:</strong> Service request descriptions, photographs, dates, and status updates</li>
            <li><strong>Communications Metadata:</strong> Timestamps, sender/recipient identifiers, and delivery status for messages sent through the platform. Message content is stored to display conversation history within the app and to deliver SMS messages on behalf of senders</li>
            <li><strong>Usage Data:</strong> Log data such as IP addresses, browser type, and pages visited, collected to maintain platform security and performance</li>
          </ul>
        </>
      )
    },
    {
      icon: Mail,
      title: 'How We Use Your Information',
      content: (
        <>
          <p className="mb-3">We use the collected information for the following purposes:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Account Management:</strong> To authenticate your identity and manage your account</li>
            <li><strong>Transactional Emails:</strong> To send account notifications and password resets</li>
            <li><strong>SMS Communications:</strong> To deliver transactional and operational rental messages between landlords and tenants through the ZenRent platform</li>
            <li><strong>Platform Operations:</strong> To process and track maintenance requests, scheduling, and notifications</li>
            <li><strong>Security:</strong> To protect against unauthorized access and maintain platform integrity</li>
          </ul>
          <p className="mt-4 text-gray-600">
            We do <strong>not</strong> sell your personal information or share it with third parties for advertising or promotional purposes.
          </p>
        </>
      )
    },
    {
      icon: Send,
      title: 'Email Communications',
      content: (
        <>
          <p className="mb-3">ZenRent uses email <strong>exclusively for transactional purposes</strong> related to your account and our services. We do not send marketing or promotional emails.</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Account Verification:</strong> Verify your email address when you create an account or update your email</li>
            <li><strong>Password Resets:</strong> Secure links to reset your password when you request a password change</li>
            <li><strong>Login Notifications:</strong> Alerts when your account is accessed from a new device or location</li>
            <li><strong>Security Alerts:</strong> Notifications about suspicious activity, or security updates affecting your account</li>
          </ul>
          <p className="mt-4 text-gray-600">
            <strong>Email Delivery:</strong> We use Amazon Web Services (AWS) Simple Email Service to deliver messages reliably and securely. Your email address is used solely for account-related communications.
          </p>
          <p className="mt-4 text-gray-600">
            <strong>Preferences:</strong> Security-related emails (password resets, account verification) cannot be opted out of as they are essential for protecting your account. You can manage security alert notification preferences in your <Link to="/dashboard" className="text-emerald-600 hover:underline">account settings</Link>.
          </p>
        </>
      )
    },
    {
      icon: Smartphone,
      title: 'SMS & Text Messaging',
      content: (
        <>
          <p className="mb-3">
            ZenRent offers optional SMS text messaging for transactional alerts and rental
            communications (including proxy messaging). SMS consent is not required to use the
            platform. Below is how we collect, use, and protect your information in connection
            with SMS messaging.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong>Program Names:</strong> ZenRent operates two SMS programs:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><strong>ZenRent Transactional Alerts</strong> — account, security, and maintenance notifications</li>
                <li><strong>ZenRent Rental Communications (Proxy)</strong> — landlord–tenant messages routed through a dedicated ZenRent number</li>
              </ul>
            </li>
            <li>
              <strong>What We Send:</strong> SMS messages include maintenance request updates, service
              scheduling confirmations, appointment changes, account and security alerts, lease and
              account notices, and direct messages between landlords and tenants facilitated through
              the ZenRent platform. ZenRent does not send marketing, promotional, or cold-outreach
              messages via SMS.
            </li>
            <li>
              <strong>Consent — Tenants:</strong> Tenants opt in to receive SMS messages by providing
              their mobile phone number and checking the SMS consent checkbox during account signup
              or in their account settings. By opting in, tenants consent to receive transactional
              and operational messages related to their rental relationship, delivered through a
              dedicated ZenRent phone number assigned to their landlord's organization. Consent is
              voluntary and provided through an explicit, affirmative action.
            </li>
            <li>
              <strong>Consent — Landlords/Property Managers:</strong> Landlords and property managers
              consent to SMS communications by providing their phone number and selecting the
              applicable SMS consent checkbox during signup or in account settings, and by enabling
              messaging for their organization. Landlords initiate messages to their tenants through
              the ZenRent platform; these messages are delivered via the organization's dedicated
              ZenRent phone number.
            </li>
            <li>
              <strong>Message Frequency:</strong> Message frequency varies. The number of messages you
              receive depends on your account activity and the communications initiated within your
              rental relationship.
            </li>
            <li>
              <strong>Message and Data Rates:</strong> Message and data rates may apply depending on
              your mobile carrier and plan. ZenRent is not responsible for any charges from your
              carrier.
            </li>
            <li>
              <strong>Opt-Out:</strong> You can stop receiving SMS messages at any time by
              replying <strong>STOP</strong> to any message from ZenRent. You may also disable SMS in
              your <Link to="/dashboard" className="text-emerald-600 hover:underline">account settings</Link>.
              After opting out, you will receive a one-time confirmation and no further SMS messages
              will be sent unless you re-enable SMS in your account settings.
            </li>
            <li>
              <strong>Help:</strong> Reply <strong>HELP</strong> to any message for assistance, or
              contact us at{' '}
              <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">support@zenrent.app</a>.
            </li>
            <li>
              <strong>SMS Data Sharing for Delivery:</strong> To deliver SMS messages, we share your
              phone number and message content with our SMS service provider, Twilio. This sharing is
              strictly for the purpose of message delivery and platform operation.
            </li>
            <li>
              <strong>No Sharing for Marketing:</strong> We will not sell, share, or disclose your
              mobile phone number, SMS consent status, or any SMS opt-in data to third parties or
              affiliates for marketing or promotional purposes. This restriction applies regardless
              of any other provision in this Privacy Policy.
            </li>
          </ul>
          <p className="mt-4 text-gray-600">
            For the full SMS terms including acceptable use and delivery disclaimers, see the{' '}
            <Link to="/terms" className="text-emerald-600 hover:underline">Terms of Service</Link>.
          </p>
        </>
      )
    },
    {
      icon: ArrowLeftRight,
      title: 'Proxy/Intermediary Messaging',
      content: (
        <>
          <p className="mb-3">
            ZenRent acts as a messaging intermediary between landlords and tenants. Understanding
            how this works is important to how your data is handled:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong>Dedicated Phone Numbers:</strong> Each landlord organization on ZenRent is
              assigned a dedicated phone number. This number is used to send and receive SMS messages
              on behalf of that organization. Tenants text this dedicated number to communicate with
              their landlord through ZenRent.
            </li>
            <li>
              <strong>Message Routing:</strong> When a landlord sends a message through ZenRent, it is
              delivered to the tenant from the organization's dedicated number. When a tenant replies,
              the message is routed back to the landlord through the platform. ZenRent processes and
              routes these messages but does not independently author content on behalf of either party.
            </li>
            <li>
              <strong>Phone Number Privacy:</strong> ZenRent does not disclose landlords' or tenants'
              personal phone numbers to the other party. All SMS communications pass through the
              dedicated ZenRent number, keeping personal contact information private.
            </li>
            <li>
              <strong>Data Used for Routing:</strong> To facilitate proxy messaging, ZenRent uses your
              phone number, organization/property association, and message content. This data is used
              solely for delivering and displaying messages within the platform and is subject to all
              protections described in this Privacy Policy.
            </li>
          </ul>
        </>
      )
    },
    {
      icon: Clock,
      title: 'Data Retention',
      content: (
        <>
          <p className="mb-3">We retain your personal data only as long as necessary to provide our services and fulfill the purposes described in this policy:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Account Data:</strong> Retained for the duration of your active account. If you request account deletion, we will remove your personal data within 30 days, except where retention is required by law</li>
            <li><strong>Message History:</strong> Conversation history is retained to provide continuity within the platform and is deleted when the associated account is deleted</li>
            <li><strong>SMS Delivery Logs:</strong> Delivery metadata (timestamps, delivery status) may be retained for up to 12 months for troubleshooting and compliance purposes</li>
            <li><strong>Legal Obligations:</strong> We may retain certain data beyond the periods above if required by applicable law, regulation, or legal process</li>
          </ul>
        </>
      )
    },
    {
      icon: Shield,
      title: 'Data Security',
      content: (
        <>
          <p className="mb-3">We implement industry-standard security measures to protect your information:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>All data is transmitted over secure HTTPS connections and encrypted at rest with AES-256 encryption</li>
            <li>Database access is restricted and monitored</li>
            <li>Regular security audits and updates are performed</li>
          </ul>
        </>
      )
    },
    {
      icon: Trash2,
      title: 'Data Deletion & Your Rights',
      content: (
        <>
          <p className="mb-3">You have control over your personal data:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Email Preferences:</strong> You can <Link to="/unsubscribe" className="text-emerald-600 hover:underline">unsubscribe from notifications</Link> at any time</li>
            <li><strong>SMS Preferences:</strong> Reply <strong>STOP</strong> to opt out of SMS messages, or manage preferences in your account settings</li>
            <li><strong>Account Deletion:</strong> Request complete deletion of your account and associated data by contacting support</li>
            <li><strong>Data Access:</strong> Request a copy of your personal data stored in our system</li>
            <li><strong>Data Correction:</strong> Update or correct your information through your account settings</li>
          </ul>
          <p className="mt-4 text-gray-600">
            Note: Some data may be retained as required by law or for legitimate business purposes
            as described in the Data Retention section above.
          </p>
        </>
      )
    },
    {
      icon: HelpCircle,
      title: 'Contact Us',
      content: (
        <>
          <p className="mb-3">
            If you have questions about this Privacy Policy, wish to exercise your data rights
            (access, correction, deletion), or have concerns about how your information is handled,
            please contact us:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Email:</strong> <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">support@zenrent.app</a></li>
          </ul>
          <p className="mt-4 text-gray-500 text-sm">
            We aim to respond to all privacy inquiries within 48 business hours.
          </p>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-semibold text-gray-900">
            Zen<span className="text-emerald-600">Rent</span>
          </Link>
          <Link 
            to="/dashboard" 
            className="text-emerald-600 hover:text-emerald-700 font-medium transition"
          >
            Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            At ZenRent, we are committed to protecting your privacy. This policy explains how we collect, 
            use, and safeguard your personal information when you use our rental property management platform.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <section 
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
                  <div className="text-gray-700 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Cookies Notice */}
        <section className="mt-8 bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookies & Local Storage</h2>
          <p className="text-gray-700 leading-relaxed">
            ZenRent uses essential cookies and local storage to maintain your session and remember your preferences. 
            We do not use third-party tracking cookies or analytics that collect personal information. 
            By using our platform, you consent to the use of these essential technologies.
          </p>
        </section>

        {/* Policy Changes */}
        <section className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal reasons. 
            We will notify you of any material changes by posting a notice on our platform or sending you an email. 
            Your continued use of ZenRent after changes are posted constitutes your acceptance of the updated policy.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} ZenRent. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/terms" className="text-gray-500 hover:text-emerald-600 transition">
                Terms &amp; Conditions
              </Link>
              <Link to="/unsubscribe" className="text-gray-500 hover:text-emerald-600 transition">
                Unsubscribe
              </Link>
              <a href="mailto:support@zenrent.app" className="text-gray-500 hover:text-emerald-600 transition">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
